import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from 'store';

export interface PendingLabOrder {
  billType?: string;
  // Captured once at dispatch time so the optimistic row sorts as the newest Active row until the
  // synced order arrives with its own dateOrdered — a fallback of "no date" would sort it to the
  // bottom instead (byDateDesc treats a missing date as epoch 0).
  createdAt: string;
  elationLabOrderId: number;
  title: string;
}

interface LabOrdersState {
  // Optimistic, just-created orders keyed by patientId. GET /lab-orders is
  // eventually consistent (Elation webhook synced), so a freshly POSTed draft
  // will not appear in the list immediately — we surface it here as a "pending
  // sync" row until the synced list catches up.
  pendingOrders: Record<string, PendingLabOrder[]>;
}

const initialState: LabOrdersState = { pendingOrders: {} };

const labOrdersSlice = createSlice({
  initialState,
  name: 'labOrders',
  reducers: {
    addPendingLabOrder: (
      state,
      action: PayloadAction<{ order: PendingLabOrder; patientId: string }>
    ) => {
      const { order, patientId } = action.payload;
      const existing = state.pendingOrders[patientId] ?? [];
      if (!existing.some((item) => item.elationLabOrderId === order.elationLabOrderId)) {
        state.pendingOrders[patientId] = [order, ...existing];
      }
    },
    removePendingLabOrders: (
      state,
      action: PayloadAction<{ elationOrderIds: string[]; patientId: string }>
    ) => {
      const { elationOrderIds, patientId } = action.payload;
      const existing = state.pendingOrders[patientId];
      if (!existing) return;
      state.pendingOrders[patientId] = existing.filter(
        (item) => !elationOrderIds.includes(String(item.elationLabOrderId))
      );
    }
  }
});

export const { addPendingLabOrder, removePendingLabOrders } = labOrdersSlice.actions;

export const selectPendingLabOrders = (patientId: string) => (state: RootState) =>
  state.labOrders.pendingOrders[patientId] ?? [];

export default labOrdersSlice.reducer;
